import { FC, ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Form from "../components-v2/Form";
import { Button, LinkButton } from "../components-v2/Button";
import dCompanyLogo from "../assets/img/brand/logoPrimary.svg";
import styles from "./LoginComponents.module.css";
import { Alert } from "../components-v2/Alert";
import BankIDSymbol from "../assets/img/BankIDSymbol.svg";
import BankIDSwedenLogo from "../assets/img/BankIDSwedenLogo.svg";
import MitIDDenmark from "../assets/img/MitIdDenmarkLogo.svg";
import { isMfaChallenge, MfaChallenge } from "../stores/UserStore";
import { Box, Card, Container, Grid, GridItem, VBox } from "../components-v2/Box";
import { MTextInput, TextInput } from "../components-v2/TextInput";
import { Icon } from "../components-v2/Icon";
import { CreateUserModel, LoginModel } from "./loginDomain";
import { observer } from "mobx-react";
import { TMessageKey } from "../i18n/types.gen";
import { useTranslation } from "../stores/RootStoreContext";
import { Markdown } from "../text/Markdown";
import { isMobileView } from "../utils/screen";

export const LoginHeader: FC<{ disableLogo?: boolean }> = ({ disableLogo }) => {
  const logo = dCompanyLogo;
  return !disableLogo ? (
    <Box justifyContent={"center"}>
      <img src={logo} alt="dCompany logo" className={styles.logo} width={350} />
    </Box>
  ) : null;
};

export const LoginWithUsername: FC<{
  state: "input" | "loading" | "error";
  onSubmit: (username: string, password: string) => void;
  onForgotPassword: () => void;
  onGoBack: () => void;
  disableLogo?: boolean;
}> = ({ state, onSubmit, onForgotPassword, onGoBack, disableLogo }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const isLoading = state === "loading";
  const isError = state === "error";
  const handleSubmit = useCallback(() => {
    onSubmit(username, password);
  }, [onSubmit, username, password]);

  return (
    <Container maxWidth={"sm"}>
      <div>
        <LinkButton label={t("back")} arrow={"left"} onClick={onGoBack} />
      </div>
      <LoginHeader disableLogo={disableLogo} />
      <Form onSubmit={handleSubmit}>
        <VBox>
          <h4>{t("login.loginByUsername")}</h4>

          <TextInput
            name={"username"}
            label={t("login.username")}
            type={"text"}
            value={username}
            error={isError}
            disabled={isLoading}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextInput
            name={"password"}
            label={t("login.password")}
            type={"password"}
            value={password}
            error={isError}
            message={isError ? t("login.incorrectData") : undefined}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
          <Button
            label={isLoading ? t("loggingIn") : t("login.login")}
            variant={"primary"}
            data-test-id="loginButton"
            loading={isLoading}
            disabled={isLoading}
            fullWidth={true}
            type={"submit"}
          />
          <Box>
            <LinkButton
              weight="normal"
              label={t("login.forgotPassword")}
              onClick={(e) => {
                e.preventDefault();
                onForgotPassword();
              }}
            />
          </Box>
          <Alert
            message={<Markdown value={t("login-help-info")} />}
            type={"info"}
            icon={"info"}
            marginTop={"lg"}
          />
        </VBox>
      </Form>
    </Container>
  );
};

export const CreateUser: FC<{
  model?: CreateUserModel;
  state: "input" | "loading" | "error";
  onSubmit: (model: CreateUserModel) => void;
  onGoBack: () => void;
  disableLogo?: boolean;
}> = ({ model, state, onSubmit, onGoBack, disableLogo }) => {
  const { t } = useTranslation();
  const isLoading = state === "loading";
  const isError = state === "error";

  if (model === undefined) {
    return null;
  }

  return (
    <Container maxWidth={"sm"}>
      <div>
        <LinkButton label={t("back")} arrow={"left"} onClick={onGoBack} />
      </div>
      <LoginHeader disableLogo={disableLogo} />
      <Form onSubmit={() => onSubmit(model)}>
        <VBox gap={"lg"}>
          <h4>{t("login.register-user")}</h4>
          <VBox>
            <MTextInput label={t("login.username")} model={model.username} disabled={true} />
            <MTextInput label={t("name")} model={model.name} disabled={isLoading} />
            <MTextInput
              type="password"
              label={t("login.password")}
              model={model.password}
              disabled={isLoading}
            />
            <MTextInput
              type="password"
              label={t("login.repeat-password")}
              model={model.repeatPassword}
              disabled={isLoading}
            />
          </VBox>
          <Button
            label={t("register")}
            variant={"primary"}
            data-test-id="loginButton"
            loading={isLoading}
            disabled={isLoading}
            fullWidth={true}
            type={"submit"}
          />
          {isError && <Alert message={{ key: "general-error" }} type={"error"} />}
        </VBox>
      </Form>
    </Container>
  );
};

function LoginMethodButton({
  icon,
  label,
  id,
  onClick,
}: {
  icon: ReactElement;
  label: string;
  id?: string;
  onClick: () => void;
}) {
  return (
    <button id={id} onClick={onClick}>
      <Card
        className={styles.buttonCard}
        justifyContent={"center"}
        alignItems={"center"}
        textAlign={"center"}
        gap={"lg"}
      >
        {icon}
        <div className={"text-lg"}>{label}</div>
      </Card>
    </button>
  );
}

export function BankIdWarning() {
  return <Alert message={{ key: "login.bankid-warning" }} type={"warning"} />;
}

export function IntroText() {
  const { t } = useTranslation();
  return (
    <Alert
      type={"info"}
      icon={false}
      message={
        <VBox>
          <p>{t("login.introText")}</p>
          <p>{t("login.introText2")}</p>
          <a className={"text-inherit"} href={"https://dcompany.no"}>
            {t("login.linkText")}
          </a>
        </VBox>
      }
    />
  );
}

export function SwedishBankIdLoginButton({
  onSelectLoginMethod,
  label,
}: {
  onSelectLoginMethod: (method: string) => void;
  label: TMessageKey;
}) {
  const { t } = useTranslation();
  return (
    <LoginMethodButton
      id="loginWithSwedishBankId"
      icon={<img src={BankIDSwedenLogo} alt="BankID" className={styles.loginMethodIcon} />}
      label={t(label)}
      onClick={() => onSelectLoginMethod("swedishBankId")}
    />
  );
}

export function DanishMitIDLoginButton({
  onSelectLoginMethod,
  label,
}: {
  onSelectLoginMethod: (method: string) => void;
  label: TMessageKey;
}) {
  const { t } = useTranslation();
  return (
    <LoginMethodButton
      id="loginWithDanishMitId"
      icon={<img src={MitIDDenmark} alt="MitID" className={styles.loginMethodIcon} />}
      label={t(label)}
      onClick={() => onSelectLoginMethod("danishMitId")}
    />
  );
}

export function NorwegianBankIdLoginButton({
  onSelectLoginMethod,
}: {
  onSelectLoginMethod: (method: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <LoginMethodButton
      id="loginWithBankid"
      icon={<img src={BankIDSymbol} alt="BankID" className={styles.loginMethodIcon} />}
      label={t("loginWithBankidNorway")}
      onClick={() => onSelectLoginMethod("norwegianBankId")}
    />
  );
}

export function UsernamePasswordLoginButton({
  onSelectLoginMethod,
}: {
  onSelectLoginMethod: (method: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <LoginMethodButton
      id="loginWithoutBankId"
      icon={<Icon name={"lock"} className={styles.loginMethodIcon} />}
      label={t("loginWithoutBankid")}
      onClick={() => onSelectLoginMethod("username")}
    />
  );
}

export const ChooseLoginMethod: FC<{
  showBankIdWarning?: boolean;
  showTimeoutMessage?: boolean;
  disableIntroText?: boolean;
  disableLogo?: boolean;
  disableUsernamePasswordLogin?: boolean;
  customHeading?: ReactNode;
  createUserModel?: CreateUserModel;
  onLoginWithUsername: (username: string, password: string) => void;
  onSelectLoginMethod: (
    method: "username" | "norwegianBankId" | "swedishBankId" | "danishMitId" | "create-user",
  ) => void;
}> = ({
  showBankIdWarning,
  showTimeoutMessage,
  disableIntroText,
  disableLogo,
  disableUsernamePasswordLogin,
  customHeading,
  createUserModel,
  onLoginWithUsername,
  onSelectLoginMethod,
}) => {
  const { t } = useTranslation();
  return (
    <Container maxWidth={"sm"} gap={"lg"} alignItems={"center"} paddingX={false}>
      <LoginHeader disableLogo={disableLogo} />
      {showTimeoutMessage ? (
        <Alert type={"warning"} message={t("login.timeout")} />
      ) : showBankIdWarning ? (
        <BankIdWarning />
      ) : !disableIntroText ? (
        <IntroText />
      ) : undefined}
      {customHeading ? customHeading : <h2>{t("chooseLoginMethod")}</h2>}
      <Grid cols={2} colsSm={disableUsernamePasswordLogin && !isMobileView() ? 3 : 2} gap={"sm"}>
        <NorwegianBankIdLoginButton
          onSelectLoginMethod={() => onSelectLoginMethod("norwegianBankId")}
        />
        <SwedishBankIdLoginButton
          onSelectLoginMethod={() => onSelectLoginMethod("swedishBankId")}
          label={"loginWithBankidSweden"}
        />
        <DanishMitIDLoginButton
          onSelectLoginMethod={() => onSelectLoginMethod("danishMitId")}
          label={"loginWithMitIdDenmark"}
        />
        {!disableUsernamePasswordLogin && (
          <GridItem textAlign={"center"}>
            <UsernamePasswordLoginButton
              onSelectLoginMethod={() => onSelectLoginMethod("username")}
            />
          </GridItem>
        )}
      </Grid>
      {createUserModel && (
        <VBox textAlign={"center"} marginBottom={"sm"}>
          <strong>{t("invitation.no-bankid.title")}</strong>
          <Markdown
            value={t("invitation.no-bankid.description", {
              username: createUserModel.username.validatedValue,
            })}
          />
          <LinkButton
            label={t("invitation.no-bankid.create-account")}
            onClick={() => onSelectLoginMethod("create-user")}
          />
        </VBox>
      )}
      {import.meta.env.DEV && !disableUsernamePasswordLogin && (
        <div>
          <SelectLoginUserForDevelopment onSelectUser={onLoginWithUsername} />
        </div>
      )}
    </Container>
  );
};

const SelectLoginUserForDevelopment: FC<{
  onSelectUser: (username: string, password: string) => void;
}> = ({ onSelectUser }) => {
  const users = [
    { name: "Kai Mossige - CLOUD VENTURES (admin)", u: "oslo", p: "norway" },
    {
      name: "Martin Lewandowski - CLOUD VENTURES (shareholder)",
      u: "04103849077",
      p: "dc0mpany4y0u",
    },
    { name: "Anette Tran - CLOUD VENTURES (board member)", u: "anette.tran", p: "dc0mpany4y0u" },
    { name: "Ingri Dyrkorn (Altinn test user)", u: "22124201644", p: "norway" },
    { name: "Fornuftig Pute (BR test user)", u: "24892649924", p: "norway" },
    { name: "Super admin", u: "superadmin", p: "dc0mpany4y0u" },
    { name: "Per Hasnotaccepted Terms", u: "per", p: "dc0mpany4y0u" },
    //unused for now, should clean up the next patch reset {name: "Naimdjon (user for searching 1881)", u: "naimdjon", p: "dc0mpany4y0u" },
    { name: "Foo Bar", u: "fb", p: "dc0mpany4y0u" },
  ];

  return (
    <VBox alignItems={"start"} marginTop={"lg"}>
      <h4>Development mode only: Choose user</h4>
      {users.map((user) => {
        return (
          <LinkButton
            key={user.name}
            label={user.name}
            onClick={() => onSelectUser(user.u, user.p)}
            icon={"user-circle"}
          />
        );
      })}
    </VBox>
  );
};

export const MfaOtpInput: FC<{
  challenge: MfaChallenge;
  state: "input" | "submitting" | "invalid-code" | "unknown-error" | "sending-new-code";
  onSubmit: (code: string) => void;
  onResendOneTimeCode: () => void;
  disableLogo?: boolean;
}> = ({ challenge, state, onSubmit, onResendOneTimeCode, disableLogo }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const inputRef = useRef<HTMLInputElement>();

  const clearAndSendNew = useCallback(() => {
    setCode("");
    onResendOneTimeCode();
    inputRef.current?.focus();
  }, [onResendOneTimeCode]);

  return (
    <Container maxWidth={"sm"}>
      <LoginHeader disableLogo={disableLogo} />
      <>
        {state === "invalid-code" || state === "sending-new-code" ? (
          <Alert
            icon={{ name: "warning", size: "lg" }}
            type={"error"}
            message={
              <VBox>
                <div>{t("login.invalid-one-time-code")}</div>
                <p>
                  <LinkButton
                    label={t("login.get-a-new-code")}
                    color={"inherit"}
                    onClick={clearAndSendNew}
                    loading={state === "sending-new-code"}
                  />
                </p>
              </VBox>
            }
          />
        ) : state === "unknown-error" ? (
          <Alert
            icon={{ size: "lg", name: "warning" }}
            type={"error"}
            message={t("login.failed-to-send-code")}
          />
        ) : (
          <Alert
            icon={{ size: "lg", name: "warning", weight: "regular" }}
            type={"info"}
            message={t("login.one-time-code-has-been-sent--email", { email: challenge.recipient })}
          />
        )}
      </>
      <Form submitOnEnter onSubmit={() => onSubmit(code)}>
        <VBox>
          <TextInput
            label={t("login.one-time-code")}
            value={code}
            error={state === "invalid-code"}
            onChange={(e) => setCode(e.target.value)}
            autofocus
          />
          <Button
            label={t("submit")}
            variant={"primary"}
            type={"submit"}
            loading={state === "submitting"}
            disabled={state === "submitting"}
          />
        </VBox>
      </Form>
    </Container>
  );
};

export const Login = observer(function Login({
  model,
  customHeading,
}: {
  model: LoginModel;
  customHeading?: ReactNode;
}) {
  useEffect(() => {
    model.initAffiliateKey();
  }, [model]);

  useEffect(() => {
    model.initRedirectUrl();
  }, [model]);

  useEffect(() => {
    model.checkBankIdStatus();
  }, [model]);

  return (
    <>
      {isMfaChallenge(model.loginStatus) ? (
        <MfaOtpInput
          challenge={model.loginStatus}
          state={model.mfaState}
          onSubmit={model.submitOneTimeCode}
          onResendOneTimeCode={model.sendNewOneTimeCode}
          disableLogo={model.disableLogo}
        />
      ) : model.loginView === "forgot-password" ? (
        <PasswordResetRequest
          state={model.passwordResetRequestState}
          onSubmit={model.passwordResetRequest}
          onGoBack={model.clearLoginMethod}
          disableLogo={model.disableLogo}
        />
      ) : model.loginView === "username" ? (
        <LoginWithUsername
          state={model.loginState}
          onGoBack={model.clearLoginMethod}
          onSubmit={model.loginWithUsername}
          onForgotPassword={model.forgotPassword}
          disableLogo={model.disableLogo}
        />
      ) : model.loginView === "create-user" ? (
        <CreateUser
          model={model.createUserModel}
          state={model.loginState}
          onGoBack={model.clearLoginMethod}
          onSubmit={model.createUser}
          disableLogo={model.disableLogo}
        />
      ) : (
        <ChooseLoginMethod
          showTimeoutMessage={model.loginStatus === "timed-out"}
          onLoginWithUsername={model.loginWithUsername}
          onSelectLoginMethod={model.selectLoginMethod}
          customHeading={customHeading}
          createUserModel={model.createUserModel}
          disableIntroText={model.disableIntroText}
          disableLogo={model.disableLogo}
          disableUsernamePasswordLogin={model.disableUsernamePasswordLogin}
          showBankIdWarning={model.showBankIdWarning}
        />
      )}
    </>
  );
});

export const PasswordResetRequest: FC<{
  state: "input" | "submitting" | "invalid-email" | "server-error" | "sent";
  onSubmit: (email: string) => void;
  onGoBack: () => void;
  disableLogo?: boolean;
}> = ({ onSubmit, onGoBack, state, disableLogo }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");

  return (
    <Container maxWidth={"sm"}>
      <div>
        <LinkButton label={t("back")} arrow={"left"} onClick={onGoBack} />
      </div>
      <LoginHeader disableLogo={disableLogo} />
      {state === "sent" ? (
        <Alert message={t("reset-password.sent", { email: email })} type={"success"} />
      ) : (
        <Form onSubmit={() => onSubmit(email)}>
          <h4>{t("reset-password.title")}</h4>
          <div>{t("reset-password.description")}</div>
          {state === "server-error" && (
            <Alert message={t("reset-password.could-not-send")} type={"error"} />
          )}
          {state === "invalid-email" && (
            <Alert message={t("email-field.invalid-email")} type={"warning"} />
          )}
          <VBox marginTop>
            <TextInput
              label={t("reset-password.email")}
              value={email}
              error={state === "invalid-email"}
              onChange={(e) => setEmail(e.target.value)}
              autofocus
            />
            <Button
              label={t("reset-password.send")}
              variant={"primary"}
              type={"submit"}
              loading={state === "submitting"}
              disabled={state === "submitting"}
            />
          </VBox>
        </Form>
      )}
    </Container>
  );
};
